import React, { useState, useEffect, useCallback } from 'react';
import { QueueDiagnosticsPanel } from './QueueDiagnosticsPanel';
import { usePipeline } from './usePipeline';

// Simple Alert/Details Modal
const DetailsModal = ({ title, content, onClose }: { title: string, content: any, onClose: () => void }) => (
    <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.8)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
        <div style={{
            background: '#1e1e1e', padding: 20, borderRadius: 8,
            maxWidth: '80vw', maxHeight: '80vh', overflow: 'auto',
            border: '1px solid #444', color: '#eee'
        }}>
            <h3 style={{ marginTop: 0 }}>{title}</h3>
            <pre style={{ background: '#000', padding: 10, overflowX: 'auto' }}>
                {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
            </pre>
            <div style={{ textAlign: 'right', marginTop: 10 }}>
                <button onClick={onClose} style={{ padding: '6px 12px' }}>Close</button>
            </div>
        </div>
    </div>
);

export const FactoryOpsConsole = ({ deployTag, diagnostics, onRun60s, onAbandonStale, onDumpQueue }: any) => {
    const { fetchDlqRecent, requeueDlq, abandonDlq, regenerateCover, rebuildThumbs, setJobParam, resetLocalState } = usePipeline();

    // DLQ State
    const [dlqEvents, setDlqEvents] = useState<any[]>([]);
    const [dlqLoading, setDlqLoading] = useState(false);
    const [activeModal, setActiveModal] = useState<any>(null); // { title: string, content: any }

    // Book Tools State
    const [toolBookId, setToolBookId] = useState('');
    const [toolSourceUrl, setToolSourceUrl] = useState('');
    const [toolStatus, setToolStatus] = useState<any>(null); // { message: string, type: 'success' | 'error' }

    // Refresh DLQ
    const refreshDlq = useCallback(async () => {
        setDlqLoading(true);
        const res = await fetchDlqRecent(50, 'open');
        if (res.ok) setDlqEvents(res.events || []);
        setDlqLoading(false);
    }, []);

    useEffect(() => {
        refreshDlq();
    }, [refreshDlq]);

    // DLQ Actions
    const handleRequeue = async (evt: any, mode: 'same' | 'repair_scan') => {
        const res = await requeueDlq(evt.id, mode);
        if (res.ok) {
            refreshDlq(); // Reload
        } else {
            alert(`Requeue Failed: ${res.error}`);
        }
    };

    const handleAbandon = async (evt: any) => {
        if (!confirm(`Are you sure you want to abandon event ${evt.id}?`)) return;
        const res = await abandonDlq(evt.id, 'manual_console');
        if (res.ok) {
            refreshDlq();
        } else {
            alert(`Abandon Failed: ${res.error}`);
        }
    };

    // Book Tools Actions
    const handleRegenCover = async () => {
        if (!toolBookId) return;
        if (!confirm(`Regenerate Master Cover for ${toolBookId}?\nThis will overwrite the existing cover using the saved prompt.`)) return;

        setToolStatus({ message: 'Regenerating...', type: 'info' });
        const res = await regenerateCover(toolBookId);
        if (res.ok) setToolStatus({ message: `Success: ${res.message}`, type: 'success' });
        else setToolStatus({ message: `Error: ${res.error}`, type: 'error' });
    };

    const handleRebuildThumbs = async () => {
        if (!toolBookId) return;
        setToolStatus({ message: 'Rebuilding...', type: 'info' });
        const res = await rebuildThumbs(toolBookId);
        if (res.ok) setToolStatus({ message: `Result: ${res.message} (${res.details})`, type: 'success' });
        else setToolStatus({ message: `Error: ${res.error}`, type: 'error' });
    };

    const handleSaveSource = async () => {
        if (!toolBookId || !toolSourceUrl) return;
        setToolStatus({ message: 'Saving...', type: 'info' });
        const res = await setJobParam(toolBookId, 'sourceUrl', toolSourceUrl);
        if (res.ok) setToolStatus({ message: 'Source URL Updated', type: 'success' });
        else setToolStatus({ message: `Error: ${res.error}`, type: 'error' });
    };

    // Derived
    const dlqCount = dlqEvents.length;
    let heartbeatStatus = 'stale';
    if (diagnostics?.lastHeartbeatAgeSeconds < 60) heartbeatStatus = 'ok';
    else if (diagnostics?.lastHeartbeatAgeSeconds < 300) heartbeatStatus = 'warn';

    const hbColor = heartbeatStatus === 'ok' ? '#4caf50' : heartbeatStatus === 'warn' ? '#ff9800' : '#f44336';


    return (
        <div style={{ padding: 20, color: '#e0e0e0', minHeight: '100dvh', overflowY: 'auto' }}>
            {/* STICKY HEADER - TRUTH PANEL */}
            <div style={{
                position: 'sticky', top: 0, background: '#111', zIndex: 100,
                padding: '10px 15px', borderBottom: '1px solid #333', marginBottom: 20,
                display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
            }}>
                {/* 1. DEPLOY TAG */}
                <div style={{ background: '#222', padding: '4px 8px', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}>
                    TAG: <span style={{ color: '#fff' }}>{deployTag || '...'}</span>
                </div>

                {/* 2. CONSUMER STATUS */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    CONSUMER:
                    <div style={{
                        padding: '2px 6px', borderRadius: 3, fontWeight: 'bold',
                        background: diagnostics?.consumerAlive ? '#1b5e20' : '#b71c1c',
                        color: typeof diagnostics?.consumerAlive === 'boolean' ? '#fff' : '#666'
                    }}>
                        {diagnostics?.consumerAlive ? 'ALIVE' : 'DEAD/UNKNOWN'}
                    </div>
                </div>

                {/* 3. HEARTBEAT */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    LAST BEAT:
                    <span style={{ color: hbColor }}>{diagnostics?.lastHeartbeatAgeSeconds ?? '?'}s ago</span>
                    {diagnostics?.lastConsumerBeatAt && (
                        <span style={{ color: '#666', fontSize: 10 }}>(Background: {Math.floor((Date.now() - new Date(diagnostics.lastConsumerBeatAt).getTime()) / 1000)}s ago)</span>
                    )}
                </div>

                {/* 4. DLQ */}
                <div style={{ fontWeight: 'bold', color: dlqCount > 0 ? '#ff5252' : '#66bb6a', fontSize: 12, marginLeft: 'auto' }}>
                    DLQ: {dlqCount} OPEN
                </div>
                <button onClick={refreshDlq} style={{ padding: '4px 12px', fontSize: 11, cursor: 'pointer' }}>Refresh</button>
            </div>


            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(400px, 2fr)', gap: 20 }}>

                {/* LEFT COLUMN */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                    {/* QUEUE CONTROLS */}
                    <div style={{ background: '#1e1e1e', padding: 15, borderRadius: 8, border: '1px solid #333' }}>
                        <h3 style={{ marginTop: 0, borderBottom: '1px solid #333', paddingBottom: 10 }}>Queue Controls</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <QueueDiagnosticsPanel
                                data={diagnostics}
                                onRun60s={onRun60s}
                                onAbandonStale={onAbandonStale}
                                onDumpQueue={onDumpQueue}
                                minimal={true}
                            />
                            <div style={{ borderTop: '1px solid #333', paddingTop: 10 }}>
                                <div style={{ fontSize: 11, color: '#888', marginBottom: 5 }}>Local Browser State</div>
                                <button
                                    onClick={() => { if (confirm("Clear local Factory cache and reload?")) resetLocalState(); }}
                                    style={{
                                        width: '100%', padding: '6px',
                                        background: '#424242', color: '#ccc',
                                        border: '1px solid #555', borderRadius: 4,
                                        cursor: 'pointer', fontSize: 11
                                    }}
                                >
                                    Reset Local State & Reload
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* BOOK TOOLS */}
                    <div style={{ background: '#1e1e1e', padding: 15, borderRadius: 8, border: '1px solid #333' }}>
                        <h3 style={{ marginTop: 0, borderBottom: '1px solid #333', paddingBottom: 10 }}>Book Tools</h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                            <div>
                                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>Target Book ID</div>
                                <input
                                    value={toolBookId} onChange={e => setToolBookId(e.target.value)}
                                    style={{ width: '100%', padding: 8, background: '#111', border: '1px solid #444', color: '#fff' }}
                                    placeholder="e.g. 12345"
                                />
                            </div>

                            <div style={{ display: 'flex', gap: 10 }}>
                                <button onClick={handleRegenCover} disabled={!toolBookId} style={{ flex: 1, padding: 8, background: '#5c6bc0', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: !toolBookId ? 0.5 : 1 }}>
                                    Regen Cover (AI)
                                </button>
                                <button onClick={handleRebuildThumbs} disabled={!toolBookId} style={{ flex: 1, padding: 8, background: '#26a69a', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: !toolBookId ? 0.5 : 1 }}>
                                    Rebuild Thumbs
                                </button>
                            </div>

                            <hr style={{ width: '100%', border: 'none', borderTop: '1px solid #333' }} />

                            <div>
                                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>Source Text URL</div>
                                <div style={{ display: 'flex', gap: 5 }}>
                                    <input
                                        value={toolSourceUrl} onChange={e => setToolSourceUrl(e.target.value)}
                                        style={{ flex: 1, padding: 8, background: '#111', border: '1px solid #444', color: '#fff' }}
                                        placeholder="https://..."
                                    />
                                    <button onClick={handleSaveSource} disabled={!toolBookId} style={{ padding: '0 12px', cursor: 'pointer' }}>Save</button>
                                </div>
                            </div>

                            {toolStatus && (
                                <div style={{ padding: 10, borderRadius: 4, background: toolStatus.type === 'error' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)', color: toolStatus.type === 'error' ? '#ff5252' : '#66bb6a', fontSize: 13 }}>
                                    {toolStatus.message}
                                </div>
                            )}

                        </div>
                    </div>
                </div>


                {/* RIGHT COLUMN: DLQ */}
                <div style={{ background: '#1e1e1e', padding: 0, borderRadius: 8, border: '1px solid #333', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: 15, borderBottom: '1px solid #333', background: '#252525' }}>
                        <h3 style={{ margin: 0 }}>Dead Letter Queue (Open)</h3>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', padding: 0 }}>
                        {dlqLoading ? <div style={{ padding: 20 }}>Loading...</div> : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead style={{ background: '#111', position: 'sticky', top: 0 }}>
                                    <tr>
                                        <th style={thStyle}>Time</th>
                                        <th style={thStyle}>Book / Method</th>
                                        <th style={thStyle}>Error</th>
                                        <th style={thStyle}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dlqEvents.length === 0 ? (
                                        <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center', color: '#666' }}>No open events</td></tr>
                                    ) : dlqEvents.map(evt => (
                                        <tr key={evt.id} style={{ borderBottom: '1px solid #333' }}>
                                            <td style={tdStyle}>
                                                <div style={{ color: '#fff' }}>{new Date(evt.createdAt).toLocaleTimeString()}</div>
                                                <div style={{ fontSize: 11, color: '#666' }}>{new Date(evt.createdAt).toLocaleDateString()}</div>
                                            </td>
                                            <td style={tdStyle}>
                                                <div style={{ color: '#81d4fa', cursor: 'pointer' }} onClick={() => setToolBookId(evt.bookId)} title="Click to use in tools">{evt.bookId || 'unknown'}</div>
                                                <div style={{ fontSize: 11, color: 'orange' }}>{evt.stage}</div>
                                            </td>
                                            <td style={tdStyle}>
                                                <div style={{ color: '#ff8a80', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
                                                    {evt.errorSummary}
                                                </div>
                                                <button onClick={() => setActiveModal({ title: `Error: ${evt.id}`, content: evt })} style={{ fontSize: 10, padding: '2px 6px', marginTop: 4, background: '#333', border: 'none', color: '#aaa', cursor: 'pointer' }}>View Details</button>
                                            </td>
                                            <td style={tdStyle}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    <button onClick={() => handleRequeue(evt, 'same')} style={{ ...actionBtn, background: '#2e7d32' }}>Requeue (Same)</button>
                                                    <button onClick={() => handleRequeue(evt, 'repair_scan')} style={{ ...actionBtn, background: '#1565c0' }}>Requeue (Repair)</button>
                                                    <button onClick={() => handleAbandon(evt)} style={{ ...actionBtn, background: '#c62828' }}>Abandon</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

            </div>

            {activeModal && <DetailsModal title={activeModal.title} content={activeModal.content} onClose={() => setActiveModal(null)} />}
        </div>
    );
};

const thStyle = { padding: '8px 12px', textAlign: 'left' as const, color: '#888', fontWeight: 500, fontSize: 12 };
const tdStyle = { padding: '8px 12px', verticalAlign: 'top' };
const actionBtn = { padding: '4px 8px', border: 'none', borderRadius: 3, color: 'white', cursor: 'pointer', fontSize: 11, textAlign: 'left' as const };
